<template>
    <div
        class="markdown-editor"
        :class="{ 'markdown-editor--wysiwyg': editorType === 'wysiwyg' && mode === 'edit' }"
    >
        <!-- VIEW MODE -->
        <Viewer
            v-if="isView"
            ref="viewerRef"
            :initial-value="value"
            :options="markdownViewerOptions"
        />

        <!-- EDIT MODE -->
        <div
            v-else
            class="markdown-editor-wrapper"
        >
            <!-- Toolbar Slot - for custom toolbars like parameter insertion -->
            <div
                v-if="$slots.toolbar"
                class="markdown-editor-toolbar"
            >
                <slot name="toolbar" />
            </div>

            <Editor
                ref="editorRef"
                :initial-value="value"
                :height="height"
                :initial-edit-type="editorType"
                :options="editorOptions"
                preview-style="tab"
                @change="handleChange"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import type { Editor as ToastEditorInstance } from '@toast-ui/editor'
import '@toast-ui/editor/dist/toastui-editor.css'

import { createWidgetRules, getEditorMarkdown } from '@/composables/useMarkdownWidgets'
import { markdownViewerOptions } from '@/composables/useMarkdownViewerOptions'

/* ----------------------------------------
 * Types
 * ---------------------------------------*/
export type Mode = 'edit' | 'view'
export type EditorType = 'markdown' | 'wysiwyg'

interface Props {
    value: string
    mode?: Mode
    height?: string
    editorType?: EditorType
    enableParameters?: boolean
}

const Editor = () => import('@toast-ui/vue-editor').then(m => m.Editor)
const Viewer = () => import('@toast-ui/vue-editor').then(m => m.Viewer)

/* ----------------------------------------
 * Props / Emits
 * ---------------------------------------*/
const props = withDefaults(defineProps<Props>(), {
    value: '',
    mode: 'edit',
    height: '400px',
    editorType: 'markdown',
    enableParameters: false
})

const emit = defineEmits<{
    (e: 'update:value', value: string): void
}>()

/* ----------------------------------------
 * State
 * ---------------------------------------*/
const isView = computed(() => props.mode === 'view')

const editorRef = ref<{
    editor: ToastEditorInstance
} | null>(null)

/**
 * Ref for the Toast UI Viewer component.
 * The Viewer wrapper does not watch `initialValue` prop changes,
 * so we call `invoke('setMarkdown', ...)` manually when the value updates.
 */
const viewerRef = ref<{ invoke: (method: string, ...args: unknown[]) => unknown } | null>(null)

/* ----------------------------------------
 * Editor options with widget rules
 * ---------------------------------------*/
const editorOptions = computed(() => {
    if (!props.enableParameters) {
        return {}
    }

    return createWidgetRules({
        onRemove: () => {
            // Simply trigger change after chip removal
            setTimeout(() => {
                handleChange()
            }, 50)
        }
    })
})

/* ----------------------------------------
 * Helpers
 * ---------------------------------------*/
function getEditorValue (): string {
    const editor = editorRef.value?.editor
    if (!editor) return ''

    if (props.enableParameters) {
        return getEditorMarkdown(editor)
    }

    return editor.getMarkdown()
}

/* ----------------------------------------
 * Editor → Parent
 * ---------------------------------------*/
const handleChange = useDebounceFn(() => {
    const value = getEditorValue()
    emit('update:value', value)
}, 300)

watch(
    () => props.value,
    (value) => {
        if (isView.value) {
            // Toast UI Viewer doesn't watch `initialValue` prop changes,
            // so we update it via the invoke API
            viewerRef.value?.invoke('setMarkdown', value ?? '')
            return
        }

        const editor = editorRef.value?.editor
        if (!editor) return

        const current = getEditorValue()
        if (current !== value) {
            editor.setMarkdown(value ?? '', false)
        }
    }
)

/* ----------------------------------------
 * Exposed Methods
 * ---------------------------------------*/
function insertText (text: string) {
    const editor = editorRef.value?.editor
    if (!editor) return

    editor.insertText(text)
    setTimeout(() => {
        handleChange()
    }, 100)
}

function focus () {
    const editor = editorRef.value?.editor
    if (!editor) return

    editor.focus()
}

function getMarkdown (): string {
    return getEditorValue()
}

function setMarkdown (value: string) {
    const editor = editorRef.value?.editor
    if (!editor) return

    editor.setMarkdown(value ?? '', false)
    // Emit the change after setting
    setTimeout(() => {
        handleChange()
    }, 100)
}

defineExpose({
    insertText,
    focus,
    getMarkdown,
    setMarkdown
})
</script>

<style lang="scss">
@import '@/assets/scss/components/_markdown-editor.scss';
</style>
